import deepmerge from 'deepmerge';

import { AppAction } from '../declarations/widget-ums';
import { EMPTY_ADDRESS_FIELD, EMPTY_FIELD } from '../constants/emptyField';
import { getTextValue } from '../constants/getFieldValues';
import areaDistricts, {
  FIAS_LEVEL_CITY,
  FIAS_LEVEL_DISTRICT,
  FIAS_LEVEL_HOUSE,
  FIAS_LEVEL_STREET,
  getOmkCodesByDistrict,
  isNewMoscowArea,
} from '../constants/address';
import { AddressFields, OMKResult } from '../../types/address';
import { transformPayloadToField } from '../helpers/fieldHelpers';
import { OMKError, OMKRequestError } from '../errors/OMK';
import { parseOMKResponse } from '../helpers/parseOmkResponse';
import { EIRCRequestError } from '../errors/EIRC';
import {
  checkAddress,
  checkBtiCode,
  ERROR,
  getAddressSuggestions,
  getAddressValueFromSuggest,
  getAreaNameByDistrict,
  getAreaShortName,
  getDistricts,
  getOMKCode,
  getOMKErrorPayload,
  getStreetSuggestions,
  getStreetValueFromSuggest,
  LOADING,
  NOT_FOUND,
  setIsAddressCompleted,
  setStreetError,
  setStreetFoundInCatalog,
  SPECIAL_VALUE,
  switchToManual,
} from '../helpers/address';
import {
  getAddress,
  getAddressSearchSuggestions,
  getSpecialCaseForParseOmkCode,
  isAddressInManualMode,
  isNewMoscow,
  isZelArea,
  missingAddressInfo,
} from '../selectors/addressUnmodeled';
import {
  addressFailedToSearchError,
  addressSearchError,
  sendStats,
} from '../helpers/stats';
import { getAddressFieldsErrors } from '../selectors/address';
import { hasErrors } from '../validators';
import { sendCMABusinessError, sendCMATechError } from '../helpers/CMAstats';

export const initialAddressInfo: AddressFields = {
  region: EMPTY_FIELD,
  area: EMPTY_ADDRESS_FIELD,
  district: EMPTY_ADDRESS_FIELD,
  settlement: EMPTY_ADDRESS_FIELD,
  city: { value: null },
  street: EMPTY_FIELD,
  house: EMPTY_FIELD,
  ownership: EMPTY_FIELD,
  structure: EMPTY_FIELD,
  building: EMPTY_FIELD,
  flat: EMPTY_FIELD,
  suggestions: [],
  address: EMPTY_FIELD,
  bti_building_code: '',
  isManualInput: false,
  isAddressCompleted: false,
  isServeByEirc: null,
  eircError: false,
  fiasLevel: 0,
  fiasId: null,
  subsequentRequestError: null,
  omk_area_code: null,
  omk_district_code: null,
  omk_street_code: null,
};

const addressModel = {
  state: { address: initialAddressInfo },
  reducers: {
    'address/fetch'(state, { payload }) {
      state.address = Object.assign({}, initialAddressInfo);
      state.address.address = { value: payload || '', error: false };
    },
    'street/fetch'(state, { payload }) {
      if (payload.length < 3) {
        state.address.suggestions = [];
      }
      state.address.street = {
        value: payload || '',
        error: false,
        isFoundInCatalog: setStreetFoundInCatalog(state.address),
      };
      state.address.isAddressCompleted = setIsAddressCompleted(state.address);
    },
    'address/reset'(state) {
      state.address = Object.assign({}, initialAddressInfo);
      return state;
    },
    'address/change'(state, { payload }) {
      Object.assign(
        state.address,
        transformPayloadToField('value', payload, state.address)
      );
      state.address.address.error = false;
      const { district, area, building, house, ...rest } = payload;
      let errorPayload: { [key in keyof AddressFields]?: boolean } = {};
      // Тут не используется Object.assign поскольку тогда типизация работает не так как ожидаешь.
      if (building !== undefined) {
        errorPayload = {
          ...errorPayload,
          house: false,
          building: false,
          street: setStreetError(state.address),
        };
      }
      if (house !== undefined)
        errorPayload = {
          ...errorPayload,
          house: false,
          building: false,
          street: setStreetError(state.address),
        };
      if (district !== undefined)
        errorPayload = {
          ...errorPayload,
          area: false,
          district: false,
          street: false,
          house: false,
          building: false,
        };
      if (area !== undefined)
        errorPayload = {
          ...errorPayload,
          area: false,
          street: false,
          house: false,
          building: false,
        };
      errorPayload = Object.keys(rest).reduce(
        (acc, key) => ({ ...acc, [key]: false }),
        errorPayload
      );
      Object.assign(
        state.address,
        transformPayloadToField('error', errorPayload, state.address)
      );

      const districts = getDistricts(payload.area ? payload.area.text : '').map(
        ({ text }) => text
      );
      const districtInNewArea =
        districts.includes(getTextValue(state.address.district)) ||
        districts.includes(getTextValue(state.address.settlement));

      if (
        payload.area &&
        !payload.district &&
        !payload.settlement &&
        !districtInNewArea
      ) {
        state.address.district.value.text = '';
        state.address.settlement.value.text = '';
      }

      if (payload.district && !payload.area) {
        state.address.area.value.text = (
          areaDistricts.find(({ districts }) =>
            districts.includes(payload.district.text)
          ) || { short: '' }
        ).short;
      }
      state.address.isAddressCompleted = setIsAddressCompleted(state.address);
      return state;
    },
    'address/suggestions/update'(state, { payload }) {
      state.address.suggestions = payload;
      return state;
    },
    'address/toggle/manualInput'(state, { payload }) {
      const nextManualInputValue = !state.address.isManualInput;
      state.address.address = EMPTY_FIELD;
      if (payload && payload.save_address) {
        state.address.isManualInput = nextManualInputValue;
        state.address.street.isFoundInCatalog = true;
        state.address.suggestions = [];
        return state;
      }
      state.address = Object.assign({}, initialAddressInfo);
      state.address.isManualInput = nextManualInputValue;
      if (nextManualInputValue) {
        state.address.isServeByEirc = false;
      } else {
        state.address.isServeByEirc = null;
      }
      return state;
    },
    'address/set'(state, { payload }) {
      if (!payload) {
        state.address = Object.assign({}, initialAddressInfo);
        return state;
      }
      if (payload.special === SPECIAL_VALUE.switchToManual) {
        const updated_address = {
          ...initialAddressInfo,
          isManualInput: true,
        };
        state.address = Object.assign({}, updated_address);
        return state;
      }
      if (payload.special === SPECIAL_VALUE.retry) {
        return state;
      }
      // Уровень детализации ФИАС 8 означает дом
      if (payload.fias_level !== FIAS_LEVEL_HOUSE) {
        return state;
      }

      const {
        area,
        city_with_type,
        village,
        street_with_type,
        district,
        settlement,
        bti_building_code,
        fias_id,
        street_type,
        fias_level,
        street, // Нам не нужно чтобы она попадала в стейт
        ...restValues
      } = payload;

      state.address.fiasLevel = fias_level;
      state.address.eirc = bti_building_code ? null : false;
      state.address.bti_building_code = bti_building_code;
      state.address.fiasId = fias_id;

      state.address.omk_district_code = null;
      state.address.omk_area_code = null;
      state.address.omk_street_code = null;

      // Я не смог придумать название получше, простите.
      const settlerict = district || settlement;
      if (!area && settlerict)
        state.address.area.value.text = getAreaNameByDistrict(settlerict);
      state.address.district.value.text = settlerict;
      if (area) state.address.area.value.text = getAreaShortName(area);
      state.address.street.value = getAddressValueFromSuggest(payload);

      Object.assign(
        state.address,
        transformPayloadToField('value', restValues, state.address)
      );
      state.address.isAddressCompleted = setIsAddressCompleted(state.address);
      const errorPayload = {
        area: false,
        district: false,
        address: false,
        street: false,
        house: false,
        building: false,
      };
      Object.assign(
        state.address,
        transformPayloadToField('error', errorPayload, state.address)
      );
      return state;
    },
    'street/set'(state, { payload }) {
      if (!payload) {
        state.address = Object.assign({}, initialAddressInfo);
        return state;
      }
      if (payload.special === SPECIAL_VALUE.retry) {
        return state;
      }
      const { bti_building_code, fias_id, fias_level } = payload;

      state.address.fiasLevel = fias_level;
      state.address.eirc = bti_building_code ? null : false;
      state.address.bti_building_code = bti_building_code;
      state.address.fiasId = fias_id;

      state.address.omk_district_code = null;
      state.address.omk_area_code = null;
      state.address.omk_street_code = null;

      state.address.street.value = getStreetValueFromSuggest(payload);
      state.address.street.isFoundInCatalog = true;
      state.address.street.error = false;
      state.address.isAddressCompleted = setIsAddressCompleted(state.address);

      return state;
    },
    'address/street/change'(state, { payload }) {
      const value = payload;
      if (!value) {
        state.address.street.value = '';
        state.address.street.isFoundInCatalog = false;
        return state;
      }
      state.address.street.value = value;
      state.address.street.isFoundInCatalog = setStreetFoundInCatalog(
        state.address
      );
      state.address.isAddressCompleted = setIsAddressCompleted(state.address);
      return state;
    },
    'address/eirc/set'(state, { payload }) {
      state.address.isServeByEirc = payload;
      return state;
    },
    'address/omk/check'(state) {
      state.address.omkError = false;
      return state;
    },
    'address/omk/set'(state, { payload }) {
      Object.assign(state.address, payload);
      state.address.isAddressCompleted = setIsAddressCompleted(state.address);
      return state;
    },
    'address/omk/setError'(state) {
      state.address.omkError = true;
    },
    'address/subsequentRequestError/set'(state, { payload: errorType }) {
      state.address.subsequentRequestError = errorType;
    },
    'address/fiasId/set'(state, { payload }) {
      state.address.fiasId = payload;
      return state;
    },
    'address/address/setError'(state, { payload }) {
      state.address.address.error = payload;
      return state;
    },
    'address/setErrors'(state, { payload }) {
      state.address = deepmerge(state.address, payload);
      return state;
    },
    'address/setAddressCompleted'(state, { payload }) {
      state.address.isAddressCompleted = payload;
      return state;
    },
    'address/set/draftData'(state, { payload }) {
      if (!payload) return;
      state.address = deepmerge(state.address, payload);
      return state;
    },
  },
  effects: {
    *'address/change'({ type, payload }, { put, select }) {
      if (!payload.district) return;
      const [model] = type.split('/');
      const isManualInput = yield select(isAddressInManualMode, { model });
      const isInNewMoscow = yield select(isNewMoscow, { model });
      const isZelAO = yield select(isZelArea, { model });
      if (!(isManualInput && (isInNewMoscow || isZelAO))) return;
      yield put({ type: 'address/omk/check' });
    },
    *'address/set'({ type, payload }, { put, select }) {
      if (!payload) return;
      if (payload.special === SPECIAL_VALUE.retry) {
        const [modelName] = type.split('/');
        const { value: payload } = yield select(
          (state) => state[modelName].address.address
        );
        yield put({ type: 'address/fetch', payload });
        return;
      }
      if (payload.fias_level === FIAS_LEVEL_HOUSE) {
        yield put({ type: 'address/omk/check' });
      }
    },
    *'street/set'({ type, payload }, { put, select }) {
      if (!payload) return;
      if (payload.special === SPECIAL_VALUE.retry) {
        const [modelName] = type.split('/');
        const { value: payload } = yield select(
          (state) => state[modelName].address.street
        );
        yield put({ type: 'street/fetch', payload });
        return;
      }
      yield put({ type: 'address/omk/check/street' });
    },
    *'address/omk/check'({ type }, { put, select }) {
      yield put({ type: 'address/subsequentRequestError/set', payload: null });
      const [model] = type.split('/');
      const isInNewMoscow = yield select(isNewMoscow, { model });
      const address = yield select(getAddress, { model });

      let districtValue = getTextValue(address.district);
      let codes = getOmkCodesByDistrict(districtValue);

      if (codes) {
        yield put({ type: 'address/omk/set', payload: codes });
        yield put({ type: 'address/eirc/check' });
        return;
      }

      try {
        let { fiasId } = address;
        const isManualInput = yield select(isAddressInManualMode, {
          model,
        });
        let result: OMKResult;
        try {
          if (!fiasId && isManualInput) {
            let demandedFiasLevel = FIAS_LEVEL_DISTRICT;
            const reStadtKreis = /городской округ/i;
            if (districtValue.match(reStadtKreis)) {
              districtValue = districtValue.replace(reStadtKreis, '').trim();
              demandedFiasLevel = FIAS_LEVEL_CITY;
            }
            const districtSearchResult = yield getAddressSuggestions(
              districtValue
            );
            const { fias_id } = districtSearchResult.find(
              ({ fias_level: fiasLevel }) => fiasLevel === demandedFiasLevel
            ) || { fias_id: null };
            fiasId = fias_id;
          }

          if (!fiasId) throw new OMKError('Отсутсвтует fias_id');
          result = yield getOMKCode(fiasId);
        } catch (error) {
          throw new OMKRequestError(error.message);
        }
        const specialCase = yield select(getSpecialCaseForParseOmkCode, {
          model,
        });
        codes = parseOMKResponse(result, specialCase);
        if (!codes.omk_street_code && !isInNewMoscow) {
          yield put({ type: 'address/omk/check/street' });
          return;
        }
        yield put({ type: 'address/omk/set', payload: codes });
        yield put({ type: 'address/eirc/check' });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        yield put({
          type: 'address/subsequentRequestError/set',
          payload: getOMKErrorPayload(error),
        });
      }
    },
    *'address/omk/check/street'({ type }, { put, select }) {
      yield put({ type: 'address/subsequentRequestError/set', payload: null });
      const [model] = type.split('/');
      const specialCase = yield select(getSpecialCaseForParseOmkCode, {
        model,
      });
      try {
        const address = yield select(getAddress, { model });
        const isZelAO = yield select(isZelArea, { model });
        const { fiasId, fiasLevel } = address;
        if (fiasLevel === FIAS_LEVEL_STREET) {
          const omkCodes = yield getOMKCode(fiasId);
          const codes = parseOMKResponse(omkCodes, specialCase);
          yield put({ type: 'address/omk/set', payload: codes });
          yield put({ type: 'address/eirc/check' });
          return;
        }
        const get_address_suggestions = yield select(
          getAddressSearchSuggestions,
          { model }
        );
        const suggest = get_address_suggestions.find(
          (suggestion) =>
            suggestion.fias_level === FIAS_LEVEL_STREET &&
            suggestion.street === address.street
        );
        if (suggest) {
          const result = yield getOMKCode(suggest.fias_id);
          const codes = parseOMKResponse(result, specialCase);
          yield put({ type: 'address/omk/set', payload: codes });
          if (isZelAO) {
            yield put({ type: 'address/eirc/set', payload: false });
            return;
          }
          yield put({ type: 'address/eirc/check' });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        yield put({
          type: 'address/subsequentRequestError/set',
          payload: getOMKErrorPayload(error),
        });
      }
    },
    *'address/eirc/check'({ type }, { put, select }) {
      yield put({ type: 'address/subsequentRequestError/set', payload: null });
      try {
        const [modelName] = type.split('/');
        const { fiasId } = yield select(getAddress, { model: modelName });
        if (!fiasId) {
          yield put({ type: 'address/eirc/set', payload: false });
          return;
        }
        const eirc: boolean = yield checkBtiCode(fiasId);
        yield put({ type: 'address/eirc/set', payload: eirc });
      } catch (error) {
        yield put({
          type: 'address/subsequentRequestError/set',
          payload: EIRCRequestError.errorName,
        });
      }
    },
    *'address/subsequentRequest/retry'({ type }, { select, put }) {
      const [modelName]: [string] = type.split('/');
      const { subsequentRequestError, isManualInput, area } = yield select(
        ({
          [modelName]: model,
        }: Record<string, Record<'address', AddressFields>>) => model.address
      );
      if (subsequentRequestError === OMKRequestError.errorName) {
        yield put({
          type: `address/omk/check${
            isManualInput && !isNewMoscowArea(getTextValue(area))
              ? '/street'
              : ''
          }`,
        });
      }

      if (subsequentRequestError === EIRCRequestError.errorName)
        yield put({ type: 'address/eirc/check' });
    },
    'address/fetch': [
      function* ({ payload = '' }: AppAction<string>, { put }) {
        if (payload.length < 3) return;
        yield put({ type: 'address/suggestions/update', payload: LOADING });
        try {
          const data = yield getAddressSuggestions(payload);
          const hasSuggestions = Array.isArray(data) && data.length;
          yield put({
            type: 'address/suggestions/update',
            payload: hasSuggestions ? data.concat(switchToManual) : NOT_FOUND,
          });
          if (!hasSuggestions) {
            sendCMABusinessError('step');
            sendStats(
              addressSearchError({ type: 'address_suggests', text: payload })
            );
          }
        } catch (error) {
          sendCMATechError('step');
          sendStats(addressFailedToSearchError('address_suggests'));
          yield put({ type: 'address/suggestions/update', payload: ERROR });
        }
      },
      { type: 'takeLatest' },
    ],
    *'address/validate'({ type }, { select, put }) {
      const [modelName]: [string] = type.split('/');

      const errors = yield select(
        ({
          [modelName]: model,
        }: Record<string, Record<'address', AddressFields>>) => {
          return getAddressFieldsErrors(model);
        }
      );

      if (hasErrors(errors)) {
        yield put({
          type: 'address/setErrors',
          payload: errors,
        });
        yield put({
          type: 'address/setAddressCompleted',
          payload: false,
        });

        yield put({ type: 'form/scrollToError' });
        return;
      }
      const isMissingAddressInfo = yield select(missingAddressInfo, {
        model: modelName,
      });

      if (isMissingAddressInfo) {
        yield put({
          type: 'address/setAddressCompleted',
          payload: false,
        });
        return;
      }
      const address = yield select(getAddress, { model: modelName });
      const isAddressCompleted = checkAddress(address);
      yield put({
        type: 'address/setAddressCompleted',
        payload: isAddressCompleted,
      });
      if (isAddressCompleted) {
        yield put({ type: 'form/saveDraft' });
      }
    },
    'street/fetch': [
      function* ({ type, payload = '' }: AppAction<string>, { select, put }) {
        if (payload.length < 3) return;
        yield put({ type: 'address/suggestions/update', payload: LOADING });

        const [model]: [string] = type.split('/');
        const isZelAO = yield select(isZelArea, { model });
        try {
          const suggestions = yield getStreetSuggestions(payload, isZelAO);
          const hasSuggestions =
            Array.isArray(suggestions) && suggestions.length;
          yield put({
            type: 'address/suggestions/update',
            payload: hasSuggestions ? suggestions : [NOT_FOUND[0]],
          });
          if (!hasSuggestions) {
            sendCMABusinessError('step');
            sendStats(
              addressSearchError({ type: 'street_suggests', text: payload })
            );
          }
        } catch (error) {
          sendCMATechError('step');
          sendStats(addressFailedToSearchError('street_suggests'));
          yield put({ type: 'address/suggestions/update', payload: ERROR });
        }
      },
      { type: 'takeLatest' },
    ],
  },
};

export default addressModel;
